import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { History, Trash2, Loader2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errText } from "@/lib/api";

export const ActivityLogTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("older_30");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/activity");
      setRows(data);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const removeOne = async (l) => {
    try {
      await api.delete(`/admin/activity/${l.log_id}`);
      setRows((list) => list.filter((r) => r.log_id !== l.log_id));
      toast.success("Log entry deleted");
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const purge = async () => {
    const body =
      mode === "all"
        ? {}
        : mode === "range"
          ? { date_from: from, date_to: to }
          : { older_than_days: Number(mode.split("_")[1]) };
    if (mode === "range" && !from && !to) return toast.error("Pick a date range first");
    const label =
      mode === "all" ? "ALL log entries" : mode === "range" ? "entries in that date range" : `entries older than ${mode.split("_")[1]} days`;
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/activity/purge", body);
      toast.success(`${data.deleted} log ${data.deleted === 1 ? "entry" : "entries"} deleted`);
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-activity-tab">
      <p className="mb-4 flex items-center gap-2 text-[13px] text-muted-foreground">
        <History className="h-4 w-4 text-primary" /> Who granted, blocked or deleted whom, plus every property and
        enquiry edit — newest first.
      </p>

      <div className="mb-5 flex flex-wrap items-end gap-3 rounded-2xl border border-border p-4" data-testid="activity-purge-panel">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">Clean up</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="h-11 w-56 rounded-lg" data-testid="activity-purge-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="older_7">Older than 7 days</SelectItem>
              <SelectItem value="older_30">Older than 30 days</SelectItem>
              <SelectItem value="older_90">Older than 90 days</SelectItem>
              <SelectItem value="range">Custom date range</SelectItem>
              <SelectItem value="all">Everything</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {mode === "range" && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-11 rounded-lg" data-testid="activity-purge-from" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-11 rounded-lg" data-testid="activity-purge-to" />
            </div>
          </>
        )}
        <Button variant="outline" className="h-11 rounded-lg text-destructive" onClick={purge} disabled={busy} data-testid="activity-purge-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
          Delete logs
        </Button>
        <span className="ml-auto text-[12px] text-muted-foreground">{rows.length} entries shown</span>
      </div>

      <div className="rounded-2xl border border-border overflow-x-auto">
        <Table className="min-w-[900px]" data-testid="admin-activity-table">
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Who</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>On</TableHead>
              <TableHead>Details</TableHead>
              <TableHead className="w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-sm text-muted-foreground">Loading…</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-sm text-muted-foreground">No activity recorded yet.</TableCell>
              </TableRow>
            ) : (
              rows.map((l) => (
                <TableRow key={l.log_id} data-testid={`admin-activity-row-${l.log_id}`}>
                  <TableCell className="text-xs whitespace-nowrap">
                    {(l.created_at || "").slice(0, 16).replace("T", " ")}
                  </TableCell>
                  <TableCell className="text-sm font-semibold">{l.actor_name || l.actor_email || "—"}</TableCell>
                  <TableCell className="text-sm">
                    <span className="rounded-full bg-muted px-2.5 py-1 text-[12px] font-bold capitalize">
                      {(l.action || "").replace(/_/g, " ")}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">
                    {l.target_label || "—"}
                    {l.target_type ? <span className="ml-1.5 text-[11px] text-muted-foreground">({l.target_type})</span> : null}
                  </TableCell>
                  <TableCell className="text-[13px] text-muted-foreground">{l.detail || "—"}</TableCell>
                  <TableCell>
                    <button
                      onClick={() => removeOne(l)}
                      className="rounded-md p-1.5 text-destructive hover:bg-destructive/10 transition-colors duration-150"
                      aria-label="Delete log entry"
                      data-testid={`activity-delete-${l.log_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ActivityLogTab;
